import { Dashboard as DashboardIcon } from '@mui/icons-material';
import { Alert, Button, Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { SidebarAccordion } from 'src/components/accordion';
import { SidebarHeader } from 'src/components/header';
import { SimpleDialog } from 'src/components/modal';
import { findMatchedAssemblyComponents } from 'src/features/assemblies/editAssemblyPage/EditAssemblyPage.helper';
import { useModal } from 'src/hooks';
import { IAssembly, IAssemblyComponent, ISectionConfig } from 'src/interfaces';
import { lang } from 'src/utils';

import { AddDefaultPanel } from './components/addDefaultPanel/AddDefaultPanel';
import { SectionDetailsSidebarProps } from './SectionDetailsSidebar.interface';
import {
  CreateButtonWrapper,
  CreateComponentButton,
  SectionContainer,
  SidebarContainer,
} from './SectionDetailsSidebar.styled';

export const SectionDetailsSidebar = (props: SectionDetailsSidebarProps) => {
  const { watch, getValues, setValue } = useFormContext<
    Partial<IAssembly> & {
      assemblyComponents: IAssemblyComponent[];
      backupAssemblyComponents: IAssemblyComponent[];
    }
  >();
  const revertAssemblyComponentModal = useModal();
  const handleEditSection = () => {
    props.handleChildEditSection(true);
  };

  const [assemblyComponents, backupAssemblyComponents] = watch([
    'assemblyComponents',
    'backupAssemblyComponents',
  ]);

  const items = [
    {
      label: 'Add default',
      content: (
        <AddDefaultPanel
          assemblyComponents={findMatchedAssemblyComponents(
            assemblyComponents,
            props.selectedSection?.layoutScreenId,
            props.selectedSection?.screenSectionId,
            ['rotation_order'],
          )}
          onChangedAddComponent={props.onChangedAddComponent}
          onUpdateRotationDuration={props.onUpdateRotationDuration}
          selectedSection={props.selectedSection}
          onReplacedComponent={props.onReplacedComponent}
          onDeletedAssemblyComponent={props.onDeleteAssemblyComponent}
          handleComponentSelect={props.handleComponentSelect}
          onReorderRotation={props.onReorderRotation}
          onSelectBackupComponent={props.onSelectBackupComponent}
          backupAssemblyComponent={findMatchedAssemblyComponents(
            backupAssemblyComponents,
            props.selectedSection?.layoutScreenId,
            props.selectedSection?.screenSectionId,
          )}
          parentAssemblyComponent={findMatchedAssemblyComponents(
            props.parentAssemblyComponent,
            props.selectedSection?.layoutScreenId,
            props.selectedSection?.screenSectionId,
            ['rotation_order'],
          )}
          editable={Boolean(getValues()?.variation_parent_id)}
          childSectionEdit={props.childSectionEdit}
          onDeletedBackupAssemblyComponent={
            props.onDeletedBackupAssemblyComponent
          }
          parentBackupAssemblyComponents={findMatchedAssemblyComponents(
            props.parentBackupAssemblyComponents,
            props.selectedSection?.layoutScreenId,
            props.selectedSection?.screenSectionId,
            ['rotation_order'],
          )}
          isCodConfirmationOpen={props.isCodConfirmationOpen}
          activeAssemblyJourney={props.activeAssemblyJourney}
          customerState={props.customerState}
          assemblyId={props.assemblyId}
          showOwnerOperatorPermissions={true}
          showSoftSellInfo={true}
          handleUpdateBackupAssemblyComponents={
            props?.handleUpdateBackupAssemblyComponents
          }
          productFilters={props?.productFilters}
          productVariationFilters={props?.productVariationFilters}
          skipProductsApiOnEmptyFilters={props?.skipProductsApiOnEmptyFilters}
          initialPanelContent={
            <>
              <Typography>{lang.addComponent}</Typography>
              <CreateButtonWrapper>
                <CreateComponentButton
                  variant="text"
                  onClick={() =>
                    props.handleComponentSelect?.(
                      props.selectedSection?.layoutScreenId as number,
                      props.selectedSection?.screenSectionId as number,
                      props.selectedSection?.sectionId as number,
                      props.selectedSection?.width as number,
                      props.selectedSection?.height as number,
                      props.selectedSection?.config as ISectionConfig,
                      props.childSectionEdit,
                    )
                  }
                  startIcon={<DashboardIcon />}>
                  {lang.selectComponent}
                </CreateComponentButton>
              </CreateButtonWrapper>
            </>
          }
        />
      ),
      disabledExpand: false,
      hasError: false,
      defaultExpanded: true,
    },
  ];
  const updateComponentIdForChildAssembly = () => {
    const updatedAssemblyComponentArray = getValues().assemblyComponents.filter(
      (item) => {
        return !(
          item.layout_screen_id === props.selectedSection?.layoutScreenId &&
          item.screen_section_id === props.selectedSection?.screenSectionId
        );
      },
    );
    const updatedBackupAssemblyComponents =
      getValues().backupAssemblyComponents.filter((item) => {
        return !(
          item.layout_screen_id === props.selectedSection?.layoutScreenId &&
          item.screen_section_id === props.selectedSection?.screenSectionId
        );
      });

    setValue('assemblyComponents', updatedAssemblyComponentArray);
    setValue('backupAssemblyComponents', updatedBackupAssemblyComponents);
  };

  const handleRevertClick = () => {
    updateComponentIdForChildAssembly();
    props.onGoBack();
  };

  const handleRevertAssemblyComponent = () => {
    handleRevertClick();
    revertAssemblyComponentModal.onCloseModal();
  };

  const filteredAssemblyComponentData = assemblyComponents.filter(
    (item) =>
      item.layout_screen_id === props.selectedSection?.layoutScreenId &&
      item.screen_section_id === props.selectedSection?.screenSectionId,
  );

  return (
    <SidebarContainer>
      <SidebarHeader onBackClick={props.onGoBack} label="Section" />
      {getValues()?.variation_parent_id !== null &&
      filteredAssemblyComponentData.length === 0 ? (
        <SectionContainer>
          <Alert severity="info">{lang.alertMessages.editSection}</Alert>
          <Button variant="text" onClick={handleEditSection}>
            {lang.editSection}
          </Button>
        </SectionContainer>
      ) : (
        getValues()?.variation_parent_id !== null &&
        filteredAssemblyComponentData.length > 0 && (
          <SectionContainer>
            <Alert severity="info">{lang.alertMessages.revertSection}</Alert>
            <Button
              variant="text"
              onClick={revertAssemblyComponentModal.onOpenModal}>
              Revert section
            </Button>
          </SectionContainer>
        )
      )}
      <SidebarAccordion items={items} />

      <SimpleDialog
        open={revertAssemblyComponentModal.open}
        cancelText={lang.cancel}
        confirmText={lang.confirm}
        title={lang.revertAssemblyComponentModal.title}
        onClose={revertAssemblyComponentModal.onCloseModal}
        onClickCancel={revertAssemblyComponentModal.onCloseModal}
        modalSize="sm"
        onClickConfirm={handleRevertAssemblyComponent}
      />
    </SidebarContainer>
  );
};
